import React, { useMemo } from "react";
import { ConnectorIcon, CONNECTOR_LETTER, GemIcon, SHIELD_BG, SHIELD_LETTER } from "@/components/TileArt";

// A read-only Jangle board for the Watch page. `tiles` maps "r,c" to
// { side: 0 | 1, letter }, side 0 being player 1 (pink, bottom).
// Without letters it draws a small thumbnail.
const PINK = "#f393a1", PINK_EDGE = "#d9677a";
const BLUE = "#4FB0F0", BLUE_EDGE = "#1F8FDB";
// A bridge tile (Bridges (trial)) as it's saved in a game.
export const BRIDGE_LETTER = "|";
const SPEC_BG = { PINK: "#98bf86", GREEN: "#969292", WALL: "#94a3b8", BRIDGE: "#f2b632", RIVER: "#2a74b5", SAND: "#a08a5c" };

// The bridge square's icon (Bridges board), as in the game.
export function BridgeIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} width="80%" height="80%" fill="none" stroke="#6b4a08" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 9h20M3 9v10M21 9v10M3 19h3v-1a6 6 0 0 1 12 0v1h3M7 9V6M12 9V6M17 9V6" />
    </svg>
  );
}

// Gem squares only matter while a game is on: a finished game's board
// leaves them out (Jack, 2026-10-18).
// `marks` (How to play diagrams) maps "r,c" to "start" (a start square),
// "bad" (a tile in a non-word, red as in the game), "hole" (a tile a POW
// blew up) or "flag" (the flag in a base).
export default function WatchBoard({ shape, tiles, added, marks, letters = true, finished = false, className = "", style }) {
  const baseOf = useMemo(() => {
    const m = {};
    shape.base.H.forEach(([r, c]) => { m[`${r},${c}`] = 0; });
    shape.base.A.forEach(([r, c]) => { m[`${r},${c}`] = 1; });
    return m;
  }, [shape]);

  const cells = [];
  for (let r = 0; r < shape.rows; r++) {
    for (let c = 0; c < shape.cols; c++) {
      const key = `${r},${c}`;
      const t = tiles[key];
      const spec = shape.spec[key];
      let bg = "#222939", inner = null, extra = {};
      if (t) {
        const shield = t.letter === SHIELD_LETTER;
        bg = t.side === 0 ? PINK : BLUE;
        extra = { boxShadow: `inset 0 -${letters ? 2 : 0}px 0 ${t.side === 0 ? PINK_EDGE : BLUE_EDGE}` };
        if (shield) extra = { background: SHIELD_BG, borderRadius: 0 };
        else if (t.letter === BRIDGE_LETTER) { if (letters) inner = <BridgeIcon />; }
        else if (letters && t.letter === CONNECTOR_LETTER) inner = <ConnectorIcon />;
        else if (letters) inner = t.letter;
      } else if (baseOf[key] !== undefined) {
        // A base is one solid block, as on the game board, so it never
        // looks like a row of blank tiles.
        bg = baseOf[key] === 0 ? PINK : BLUE;
        extra = { borderRadius: 0, boxShadow: `0 0 0 ${letters ? 1 : 0.5}px ${bg}` };
      } else if (spec === "SHIELD") {
        if (!finished) {
          if (letters) inner = <GemIcon className="p-[12%]" />;
          else bg = "#7b3fbf";
        }
      } else if (SPEC_BG[spec]) {
        bg = SPEC_BG[spec];
        if (spec === "RIVER") extra = { borderRadius: 0, boxShadow: `0 0 0 ${letters ? 1 : 0.5}px ${bg}`, background: "linear-gradient(180deg,#d9c08a 0 12%,#7cb46a 12% 18%,#4a9be0 18%,#6cb6ee 50%,#4a9be0 82%,#7cb46a 82% 88%,#d9c08a 88%)" };
        if (spec === "SAND") extra = { borderRadius: 0, boxShadow: `0 0 0 ${letters ? 1 : 0.5}px ${bg}` }; // one unbroken strip
        if (letters && spec === "BRIDGE") inner = <BridgeIcon />;
        else if (letters && spec !== "WALL" && spec !== "RIVER" && spec !== "SAND") inner = <img src="/brand/pow-hammer.svg" alt="" className="w-[80%]" />;
      }
      const mark = marks?.[key];
      if (mark === "bad" && t) { bg = "#e5484d"; extra = { boxShadow: "inset 0 -2px 0 #b4232a" }; }
      else if (mark === "start" && !t) extra = { ...extra, boxShadow: "inset 0 0 0 2px #fde047" };
      else if (mark === "hole" && !t) { extra = { ...extra, boxShadow: "inset 0 0 0 2px #ff8a3d" }; inner = <span style={{ fontSize: "0.9em" }}>💥</span>; }
      else if (mark === "flag") inner = <span style={{ fontSize: "1.1em", textShadow: "none" }}>🚩</span>;
      const isNew = added?.has(key);
      cells.push(
        <div
          key={key}
          className={`aspect-square flex items-center justify-center text-white font-black leading-none ${letters ? "rounded-[3px]" : "rounded-[1px]"}`}
          style={{
            background: bg, ...extra,
            fontFamily: "Arial, Helvetica, sans-serif",
            fontSize: `calc(100cqw / ${shape.cols} * 0.52)`,
            textShadow: "0 1px 2px rgba(0,0,0,.45)",
            outline: isNew ? "2px solid #fde047" : undefined,
            outlineOffset: isNew ? "-1px" : undefined,
            position: "relative", zIndex: isNew ? 1 : undefined,
          }}
        >
          {inner}
        </div>
      );
    }
  }
  return (
    <div
      className={`grid rounded-xl bg-[#141822] ${letters ? "gap-[2px] p-1.5" : "gap-px p-1"} ${className}`}
      style={{
        gridTemplateColumns: `repeat(${shape.cols}, minmax(0, 1fr))`, containerType: "inline-size",
        // Infinity: the joined side edges are purple, as in the game.
        ...(shape.wrap ? { borderLeft: "3px solid #8e6cf0", borderRight: "3px solid #8e6cf0" } : {}),
        ...style,
      }}
    >
      {cells}
    </div>
  );
}
